import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "Federal reforms | How revenue works",
  "includeToc": true,
  "description": "The federal government reforms laws and regulations by enacting new legislation and proposing new rules to implement the legislation. Reforms are recommended by oversight organizations, such as the Inspector General or the Government Accountability Office.",
  "tags": ["How revenue works", "Federal reforms", "Deepwater horizon oil spill", "OIG reports", "GAO reports", "Dodd-Frank Act", "Reforms"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/GOMESA/how-revenue-works/"
      }}>{`How revenue works`}</a>{` /`}</p>

    <br />
    <h1 {...{
      "id": "Federal-reforms",
      "style": {
        "position": "relative"
      }
    }}>{`Federal reforms`}</h1>
    <p>{`The federal government reforms laws and regulations, as needed.  They do this by creating new legislation and new implementation rules for the legislation.`}</p>
    <h2 {...{
      "id": "Oversight-organizations",
      "style": {
        "position": "relative"
      }
    }}>{`Oversight organizations`}</h2>
    <p>{`The Office of Inspector General (OIG) and the Government Accountability Office (GAO) recommend reforms. Congress uses these recommendations to institute reforms via legislation.`}</p>
    <h3 {...{
      "id": "Office-of-Inspector-General-OIG",
      "style": {
        "position": "relative"
      }
    }}>{`Office of Inspector General (OIG)`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.doioig.gov/"
      }}>{`The Office of Inspector General `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` (OIG) oversees and promotes of excellence, integrity, and accountability within the Department of the Interior. OIG also identifies and prevents fraud, waste, and mismanagement within the agency.`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.doioig.gov/reports"
      }}>{`Review OIG reports `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <h3 {...{
      "id": "Government-Accountability-Office-GAO",
      "style": {
        "position": "relative"
      }
    }}>{`Government Accountability Office (GAO)`}</h3>
    <p>{`The Government Accountability Office (GAO) is an independent, nonpartisan agency. The GAO investigates how the federal government spends taxpayer funds.  These funds include those for natural resource management on federal and Indian lands.`}</p>
    <h3 {...{
      "id": "Proposed-rules",
      "style": {
        "position": "relative"
      }
    }}>{`Proposed rules`}</h3>
    <p>{`Congress can pass legislation to direct agencies to propose rules. This allows Congress to use the agency’s expertise to fill in technical details.`}</p>
    <br />
Per the Administrative Procedures Act, agencies propose rules to implement federal laws. The public can comment on all proposed rules before an agency finalizes them.
    <br /><br />
For more details, search the <a href="https://www.federalregister.gov/documents/search?conditions%5Bagencies%5D%5B%5D=interior-department&conditions%5Btype%5D%5B%5D=PRORULE">Federal Registrar for Department of the Interior proposed rules <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>.
    <br />
    <h2 {...{
      "id": "Reform-in-action",
      "style": {
        "position": "relative"
      }
    }}>{`Reform in action`}</h2>
    <p>{`The federal government has passed many laws that govern natural resource extraction on federal lands.  These rules affect extraction, financial practices, and more.`}</p>
    <h3 {...{
      "id": "Deepwater-Horizon-oil-spill-reforms",
      "style": {
        "position": "relative"
      }
    }}>{`Deepwater Horizon oil spill reforms`}</h3>
    <p>{`After the `}<a href="http://www.gpo.gov/fdsys/pkg/GPO-OILCOMMISSION/pdf/GPO-OILCOMMISSION.pdf">{`Deepwater Horizon oil spill (PDF) `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` in the Gulf of Mexico in 2010, the federal government reorganized the Department of the Interior. They wanted to improve oversight for offshore oil and gas extraction. They changed rules for leasing, regulation, and collection of revenue for offshore oil and gas.`}</p>
    <br />
The post-Deepwater Horizon reorganization separated and established <a href="http://www.boem.gov/about-boem/regulations-guidance/regulatory-reforms">independent oversight for offshore leasing <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>. In May and June 2010, they eliminated the Minerals Management Service (MMS). Two new bureaus and one new office were created.
    <ul>
      <li parentName="ul"><a href="https://www.boem.gov"> The Bureau of Ocean Energy Management <OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a> (<GlossaryTerm mdxType="GlossaryTerm">BOEM</GlossaryTerm>) manages development of the U.S. Outer Continental Shelf energy and mineral resources. </li>
      <li parentName="ul"><a href="https://www.bsee.gov/">The Bureau of Safety and Environmental Enforcement <OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a> (<GlossaryTerm mdxType="GlossaryTerm">BSEE</GlossaryTerm>) oversees safety of ocean energy development and production. </li>
      <li parentName="ul"><a href="https://www.onrr.gov">The Office of Natural Resources Revenue <OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a> (ONRR) collects and accounts for revenue generated from natural resource development on federal and Indian lands.</li>
    </ul>
    <p>{`The Secretary of the Interior's goals in eliminating MMS were to:`}</p>
    <ul>
      <li parentName="ul">{`Separate the three responsibilities of leasing, regulation, and revenue collection.`}</li>
      <li parentName="ul">{`Provide each office and bureau with the independence and resources necessary to fulfill their missions.`}</li>
      <li parentName="ul">{`Eliminate real and perceived conflicts associated with the previous organizations.`}</li>
    </ul>
    <p>{`The federal government made regulatory reforms following the spill. Congress did not change any laws related to offshore fossil fuel management in response to the accident.`}</p>
    <h4 {...{
      "id": "Regulation-following-the-spill",
      "style": {
        "position": "relative"
      }
    }}>{`Regulation following the spill`}</h4>
    <p>{`In May of 2018, BSEE submitted `}<a parentName="p" {...{
        "href": "https://www.federalregister.gov/documents/2018/07/05/2018-14483/oil-and-gas-and-sulfur-operations-in-the-outer-continental-shelf-blowout-preventer-systems-and-well"
      }}>{`a proposed rule revising regulations for well control and blowout preventer systems `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` put in place after the Deepwater Horizon oil spill. In May of 2019, BSEE issued the final rule.`}</p>
    <ul>
      <li parentName="ul">{`BSEE `}<a parentName="li" {...{
          "href": "https://www.bsee.gov/guidance-and-regulations/Regulations/regulatory-reform/bsee-well-control-rule-2019"
        }}>{`2019 Well Control Rule and supporting documents `}<OpenInNewIcon style={{
            fontSize: 20
          }} mdxType="OpenInNewIcon" /></a>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "The-Dodd-Frank-Act",
      "style": {
        "position": "relative"
      }
    }}>{`The Dodd-Frank Act`}</h3>
    <p>{`In 2010, the U.S. enacted the `}<a parentName="p" {...{
        "href": "http://www.gpo.gov/fdsys/pkg/PLAW-111publ203/pdf/PLAW-111publ203.pdf"
      }}>{`Dodd-Frank Wall Street Reform and Consumer Protection Act (PDF) `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` (124 Stat. 1376) to improve transparency and accountability across the financial system. It requires extractive industries companies registered with the Securities and Exchange Commission (SEC) to disclose information about payments to governments around the world.`}</p>
    <h4 {...{
      "id": "Section-1504-of-the-Dodd-Frank-Act",
      "style": {
        "position": "relative"
      }
    }}>{`Section 1504 of the Dodd-Frank Act`}</h4>
    <p>{`Section 1504 requires companies to disclose the type and total amount of payments made for commercial development of oil, natural gas, or minerals. It mandates the disclosure of taxes, royalties, fees, entitlements, bonuses, and other material benefits.`}</p>
    <br />
Section 1504 references the guidelines of the Extractive Industries Transparency Initiative (EITI). The United States participated in EITI as an implementing country from 2014 to 2017. In November 2017, the U.S. withdrew as an implementing country from EITI.
    <h4 {...{
      "id": "Section-1504-resolution",
      "style": {
        "position": "relative"
      }
    }}>{`Section 1504 resolution`}</h4>
    <p>{`In early 2017, `}<a parentName="p" {...{
        "href": "https://www.congress.gov/bill/115th-congress/house-joint-resolution/41/"
      }}>{`Congress voted to disapprove an SEC rule implementing Section 1504 of the Dodd-Frank Act `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`. The President signed the resolution on February 14, 2017. This resolution prevents the SEC from enforcing Section 1504 of Dodd-Frank until a new rule is published.`}</p>
    <h2 {...{
      "id": "Additional-laws-regulations-and-penalties",
      "style": {
        "position": "relative"
      }
    }}>{`Additional laws, regulations, and penalties`}</h2>
    <p>{`For a comprehensive list of regulations governing the Department of the Interior, explore the `}<a parentName="p" {...{
        "href": "https://revenuedata.doi.gov/how-revenue-works/federal-laws/"
      }}>{`federal laws page`}</a>{`.`}</p>
    <br /><br />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      